import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MapPin, Clock, Calendar, DollarSign, Edit3, CheckCircle, ChevronDown, ChevronUp } from "lucide-react"
import { Match, MatchScore } from '../../types'

interface ImprovedMatchHeaderProps {
  match: Match
  matchScore?: MatchScore | null
  reportCompleted: boolean
  userIsCreator: boolean
  onEditLocation: () => void
  compact?: boolean
}

export const ImprovedMatchHeader: React.FC<ImprovedMatchHeaderProps> = ({
  match,
  matchScore,
  reportCompleted,
  userIsCreator,
  onEditLocation,
  compact = false,
}) => {
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(false);

  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    if (isNaN(date.getTime())) return "Fecha invalida"
    return date.toLocaleDateString("es-ES", {
      weekday: "short",
      day: "numeric",
      month: "short",
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "FINISHED":
        return { color: '#4CAF50', backgroundColor: 'rgba(76, 175, 80, 0.1)' }
      case "IN_PROGRESS":
        return { color: '#FF9800', backgroundColor: 'rgba(255, 152, 0, 0.1)' }
      default:
        return { color: '#2196F3', backgroundColor: 'rgba(33, 150, 243, 0.1)' }
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case "FINISHED": return "Finalizado"
      case "IN_PROGRESS": return "En Progreso"
      default: return "Programado"
    }
  }

  if (compact) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        height: 36,
        padding: '0 10px',
      }}>
        {/* Team names */}
        <span style={{ fontSize: 13, fontWeight: 600, color: '#FFFFFF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>
          {match.team1Name} vs {match.team2Name}
        </span>

        {/* Status dot */}
        <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: getStatusColor(match.status).color, flexShrink: 0 }} title={getStatusText(match.status)} />

        {match.venueBooking && (
          <div
            onClick={() => {
              const vb = match.venueBooking!;
              const complexId = vb.venue.complexId || vb.venue.complex?.id;
              navigate(complexId ? `/venues/${vb.venue.id}?complex=${complexId}` : `/venues/${vb.venue.id}`);
            }}
            style={{ fontSize: 10, fontWeight: 600, color: '#00E676', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            Reservada
          </div>
        )}

        {/* Meta info - desktop only */}
        <span style={{ fontSize: 11, color: '#666', whiteSpace: 'nowrap' }}>
          {match.time} · {formatDate(match.date)} · ${match.price}
        </span>

        {/* Spacer */}
        <div style={{ flex: 1 }} />
      </div>
    )
  }

  // Original full header (non-compact mode) — kept for backwards compat
  return (
    <div style={{
      backgroundColor: '#23272A',
      border: '1px solid #33383B',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '12px',
            flexWrap: 'wrap'
          }}>
            <h1 style={{
              fontSize: '28px',
              fontWeight: 'bold',
              color: '#FFFFFF',
              margin: 0
            }}>
              {match.team1Name} vs {match.team2Name}
            </h1>
            <div style={{
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              ...getStatusColor(match.status)
            }}>
              {getStatusText(match.status)}
            </div>
            {match.venueBooking && (
              <div
                onClick={() => {
                  const vb = match.venueBooking!;
                  const complexId = vb.venue.complexId || vb.venue.complex?.id;
                  if (complexId) {
                    navigate(`/venues/${vb.venue.id}?complex=${complexId}`);
                  } else {
                    navigate(`/venues/${vb.venue.id}`);
                  }
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#00E676',
                  backgroundColor: 'rgba(0, 230, 118, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                }}
              >
                <CheckCircle size={14} />
                Cancha Reservada
              </div>
            )}
          </div>
        </div>

        {userIsCreator && (
          <button
            onClick={onEditLocation}
            style={{
              padding: '12px',
              backgroundColor: 'transparent',
              border: '1px solid #33383B',
              borderRadius: '8px',
              cursor: 'pointer',
              color: '#FFFFFF',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#33383B'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            title="Editar datos del partido"
            aria-label="Editar datos del partido"
          >
            <Edit3 size={20} />
          </button>
        )}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '16px'
      }}>
        <DetailCard icon={<MapPin size={20} color="#00E676" />} label="Ubicacion" value={match.location} large />
        <DetailCard icon={<Calendar size={20} color="#00E676" />} label="Fecha" value={formatDate(match.date)} large />
        <DetailCard icon={<Clock size={20} color="#00E676" />} label="Horario" value={match.time} large />
        <DetailCard icon={<DollarSign size={20} color="#00E676" />} label="Costo" value={`$${match.price}`} large />
      </div>
    </div>
  )
}

const DetailCard: React.FC<{ icon: React.ReactNode; label: string; value: string; large?: boolean }> = ({ icon, label, value, large }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: large ? 12 : 8,
    padding: large ? 16 : '8px 10px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
    border: '1px solid #33383B'
  }}>
    {icon}
    <div>
      <p style={{
        fontSize: large ? 12 : 10,
        color: '#B0B0B0',
        margin: '0 0 2px 0',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>{label}</p>
      <p style={{
        fontSize: large ? 16 : 13,
        fontWeight: 600,
        color: '#FFFFFF',
        margin: 0
      }}>{value}</p>
    </div>
  </div>
)
